import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Calendar, Share2, ChevronLeft, ChevronRight, Download, Maximize2, X } from "lucide-react";

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="max-w-3xl mx-auto container-x py-24 text-center">
      <p className="text-brand-navy/70">Articolul nu a fost găsit.</p>
      <button onClick={() => navigate("/articole")} className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-navy text-white font-semibold">
        <ArrowLeft className="w-4 h-4" /> Înapoi la articole
      </button>
    </div>
  );

  if (!post) return <div className="max-w-3xl mx-auto container-x py-24 text-brand-navy/60">Se încarcă…</div>;

  const hasSlides = Array.isArray(post.slides) && post.slides.length > 0;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const resolveUrl = (u) => (u && u.startsWith("/") ? `${apiBase}${u}` : u);

  // Convert plain content with newlines into HTML paragraphs
  const paragraphs = (post.content || "").split(/\n\n+/).map((p) => p.trim()).filter(Boolean);

  return (
    <article className="bg-white">
      <div className="max-w-5xl mx-auto container-x py-12 sm:py-16">
        <Link to="/articole" className="inline-flex items-center gap-2 text-brand-navy/70 hover:text-brand-navy text-sm font-semibold">
          <ArrowLeft className="w-4 h-4" /> Toate articolele
        </Link>

        <header className="mt-8 max-w-3xl">
          <div className="flex items-center gap-2 text-xs text-brand-navy/55 font-medium">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(post.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}
          </div>
          <h1 className="mt-3 font-display font-extrabold text-brand-navy text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-5 text-brand-navy/70 text-lg font-light leading-relaxed">{post.excerpt}</p>
          )}
        </header>

        {hasSlides ? (
          <SlidesViewer slides={post.slides.map(resolveUrl)} pdfUrl={resolveUrl(post.pdf_url)} title={post.title} />
        ) : (
          post.cover_image && (
            <img src={resolveUrl(post.cover_image)} alt={post.title} className="mt-10 w-full rounded-2xl shadow-soft" draggable={false} />
          )
        )}

        <div className="mt-10 prose-content space-y-6 text-brand-navy/85 text-[17px] leading-[1.75] max-w-3xl">
          {paragraphs.map((p, i) => {
            if (/^\d+\.\s/.test(p) && p.length < 120) {
              return <h2 key={i} className="font-display font-bold text-brand-navy text-2xl sm:text-3xl tracking-tight !mt-12 !mb-4">{p}</h2>;
            }
            if (p.includes("\n")) {
              const lines = p.split("\n").filter(Boolean);
              return (
                <div key={i}>
                  {lines.map((line, j) => {
                    if (line.endsWith(":")) {
                      return <p key={j} className="font-semibold text-brand-navy">{line}</p>;
                    }
                    if (line.endsWith(";") || (line.endsWith(".") && lines.length > 2)) {
                      return <p key={j} className="ml-5 relative before:content-['•'] before:absolute before:-left-4 before:text-brand-red before:font-bold">{line.replace(/[;.]$/, "")}</p>;
                    }
                    return <p key={j}>{line}</p>;
                  })}
                </div>
              );
            }
            return <p key={i}>{p}</p>;
          })}
        </div>

        <div className="mt-14 pt-8 border-t border-brand-beige flex items-center justify-between flex-wrap gap-4">
          <Link to="/articole" className="inline-flex items-center gap-2 text-brand-navy/70 hover:text-brand-navy text-sm font-semibold">
            <ArrowLeft className="w-4 h-4" /> Toate articolele
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-brand-navy/60 font-semibold uppercase tracking-widest"><Share2 className="w-3.5 h-3.5 inline mr-1"/>Distribuie</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-brand-navy text-white text-xs font-semibold hover:bg-brand-navyDeep"
            >Facebook</a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(post.title + " — " + window.location.href)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-brand-green text-white text-xs font-semibold hover:opacity-90"
            >WhatsApp</a>
          </div>
        </div>
      </div>
    </article>
  );
}

function SlidesViewer({ slides, pdfUrl, title }) {
  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const goPrev = useCallback(() => setIdx((i) => (i === 0 ? slides.length - 1 : i - 1)), [slides.length]);
  const goNext = useCallback(() => setIdx((i) => (i === slides.length - 1 ? 0 : i + 1)), [slides.length]);

  // Keyboard navigation when fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen, goPrev, goNext]);

  const total = slides.length;
  const current = slides[idx];

  return (
    <div className="mt-10">
      {/* Main viewer */}
      <div className="relative bg-brand-cream rounded-2xl overflow-hidden shadow-soft border border-brand-beige" data-testid="article-slideshow">
        <div className="relative aspect-[16/9] bg-[#FAF1E1] flex items-center justify-center">
          <img
            src={current}
            alt={`Slide ${idx + 1} din ${total} — ${title}`}
            className="w-full h-full object-contain"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            data-testid={`slide-${idx + 1}`}
          />

          {/* Prev / Next buttons */}
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-brand-navy shadow-lg flex items-center justify-center transition-colors"
            aria-label="Slide anterior"
            data-testid="slide-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-brand-navy shadow-lg flex items-center justify-center transition-colors"
            aria-label="Slide următor"
            data-testid="slide-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Counter */}
          <div className="absolute top-3 left-3 bg-brand-navy/85 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
            {idx + 1} / {total}
          </div>

          {/* Fullscreen */}
          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className="absolute top-3 right-3 bg-white/90 hover:bg-white text-brand-navy w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-colors"
            aria-label="Vizualizare pe ecran complet"
            data-testid="slide-fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Bottom strip: thumbnails + actions */}
        <div className="bg-white border-t border-brand-beige p-3 sm:p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2 overflow-x-auto flex-1 min-w-0 pb-1">
              {slides.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                    i === idx ? "border-brand-red shadow-md" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Mergi la slide ${i + 1}`}
                  data-testid={`slide-thumb-${i + 1}`}
                >
                  <img src={s} alt="" className="h-14 w-auto block" draggable={false} />
                </button>
              ))}
            </div>
            {pdfUrl && (
              <a
                href={pdfUrl}
                download
                className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navyDeep transition-colors"
                data-testid="slide-pdf-download"
              >
                <Download className="w-4 h-4" /> PDF
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[100] bg-brand-navy/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          data-testid="slide-fullscreen-overlay"
        >
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
            aria-label="Închide"
            data-testid="slide-fullscreen-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={current}
            alt={`Slide ${idx + 1} din ${total}`}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
            aria-label="Următor"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/15 text-white text-sm font-semibold px-4 py-2 rounded-full backdrop-blur-sm">
            {idx + 1} / {total}
          </div>
        </div>
      )}
    </div>
  );
}
